import { LTLCustomerStaff } from 'lib/api/getLTLCustomerStaff';
import { LTLQuickQuoteStop } from 'lib/api/getLTLQuickQuotes';
import { BatchLTLReplySummary } from 'types/BatchLTLReply';
import { Maybe } from 'types/UtilityTypes';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';

import { LTLCustomer, LTLQuote, QuickQuoteInputs } from '../types';
import { LTLExternalQuote } from '../utils/classifyLTLQuoteResult';

export enum BatchLTLShipmentStatus {
  Invalid = 'invalid',
  Ready = 'ready',
  Rating = 'rating',
  Rated = 'rated',
  // Rated and included in a reply the rep drafted, sent or copied.
  InReply = 'in_reply',
  Failed = 'failed',
}

// Missing-field names double as the rep-facing "Missing: ..." copy.
export enum BatchLTLMissingField {
  Origin = 'origin',
  Destination = 'destination',
  Items = 'items',
  Description = 'description',
  Pieces = 'pieces',
  Weight = 'weight',
  FreightClass = 'freight class',
  PickupWindow = 'pickup window',
  AccessorialDetails = 'accessorial details',
}

// One shipment is a single LTL Quick Quote form, tied to its quote request.
export type BatchLTLShipmentInput = QuickQuoteInputs & {
  // Stable card id: the quote request id for shipments from the email, a negative id for
  // shipments the rep added.
  key: number;
  // 0 for an added shipment until rating creates its quote request.
  quoteRequestId: number;
  label: string;
  // Shipper notes for the rep, e.g. "do not stack"; never sent to the TMS.
  notes: string;
};

export type BatchLTLShipmentValidation = {
  isValid: boolean;
  missingFields: BatchLTLMissingField[];
  // RHF paths relative to the shipment, e.g. `ltl.items.0.weight`.
  errorPaths: Set<string>;
};

export type BatchLTLShipmentRun = {
  status:
    | BatchLTLShipmentStatus.Rating
    | BatchLTLShipmentStatus.Rated
    | BatchLTLShipmentStatus.Failed;
  quotes: LTLQuote[];
  defaultSelectedQuoteIds: string[];
  // The quote the job saved in the TMS: link, status and carrier assignment.
  externalQuote: LTLExternalQuote;
  failureMessage: Maybe<string>;
  // The LTL job on the server; its result can be re-read by id after the rep comes back.
  jobId: Maybe<string>;
  startedAt: Maybe<number>;
  stops: LTLQuickQuoteStop[];
};

// Runs, selections and saved state are keyed by shipment key.
export type BatchLTLRunsByKey = Record<number, BatchLTLShipmentRun>;

// A run as saved in the browser: the job pointer, never the quotes (the server has them).
export type BatchLTLPersistedRun = Pick<
  BatchLTLShipmentRun,
  'status' | 'jobId' | 'startedAt' | 'stops' | 'failureMessage'
>;

// The rep's work on one batch email, saved so it survives leaving the email. Shipments are
// whole Quick Quote values keyed by shipment key, so the panel's components can change
// without changing what is saved. Shipments the rep added are saved whole.
export type BatchLTLPersistedState = {
  schemaVersion: number;
  savedAt: number;
  emailId: number;
  suggestionIds: number[];
  suggestions: SuggestedQuoteChange[];
  shipments: BatchLTLShipmentInput[];
  // Paths (relative to the shipment) the rep changed; only these override new defaults.
  editedFields: Record<number, string[]>;
  ltlCustomer: Maybe<LTLCustomer>;
  ltlCustomerStaff: Maybe<LTLCustomerStaff>;
  runs: Record<number, BatchLTLPersistedRun>;
  quoteSelectionOverrides: Record<number, string[]>;
  expandedQrIds: number[];
  // Quote requests accepted before the mail client completes. These remain retryable if
  // creating or sending the reply fails.
  acceptedQrIds: number[];
  repliedQrIds: number[];
  editedDraft: Maybe<string>;
  replySummary: Maybe<BatchLTLReplySummary>;
  // Keys of shipments the rep took out of the batch.
  removedQrIds: number[];
  // Keys of shipments the rep left out of the reply.
  excludedFromReplyQrIds: number[];
};

export enum BatchLTLStackableEdit {
  Unchanged = 'unchanged',
  Stackable = 'stackable',
  NotStackable = 'not_stackable',
}

// Empty values leave the field unchanged on every selected shipment.
export type BatchLTLBulkEditValues = {
  originLocation: string;
  destinationLocation: string;
  pickupDate: Maybe<Date>;
  pickupCloseDate: Maybe<Date>;
  freightClass: string;
  addAccessorialCode: string;
  removeAccessorialCode: string;
  stackable: BatchLTLStackableEdit;
};
